<template>
  <HeaderAnonymousProfileSkeleton v-if="isLoading" />
  <NuxtLink v-else class="header-anonymous-profile" :to="link">
    <div
      class="header-anonymous-profile__avatar"
      :class="{ 'header-anonymous-profile__avatar--has-avatar': hasWarRoomProfile }"
    >
      <template v-if="hasWarRoomProfile">
        <Avatar type="next-medium-md" :title="initials" :src="currentUser.warRoomAvatarUrl" />
      </template>
      <EyeSlashIcon v-else class="header-anonymous-profile__icon" width="24" height="24" />
    </div>
    <div class="header-anonymous-profile__profile">
      <BravadoText tag="p" variant="subhead-semibold">{{ username }}</BravadoText>
      <BravadoText v-if="hasWarRoomProfile" class="header-anonymous-profile__description" tag="p" variant="caption1">
        {{ position }}
      </BravadoText>
    </div>
  </NuxtLink>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import type { Location } from 'vue-router';
import HeaderAnonymousProfileSkeleton from './HeaderAnonymousProfileSkeleton.vue';
import { WAR_ROOM_PROFILE_EDIT_PAGE, WAR_ROOM_PROFILE_USERNAME_PAGE } from '@/constants/pages_generated';
import type { CurrentUser } from '@/types/graphql';
import Avatar from '~/components/common/Avatar/Avatar.vue';

export default defineComponent({
  name: 'HeaderAnonymousProfile',
  components: {
    EyeSlashIcon: () => import('@/assets/svg/eye-slash.vue.svg'),
    HeaderAnonymousProfileSkeleton,
    Avatar,
  },
  props: {
    currentUser: {
      type: Object as () => CurrentUser | null,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasWarRoomProfile: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    username(): string {
      return this.hasWarRoomProfile ? `@${this.currentUser?.username}` || '' : 'Anonymous profile';
    },
    initials(): string {
      return this.currentUser?.username?.substring(0, 1)?.toLocaleUpperCase() || '';
    },
    link(): Location {
      return this.hasWarRoomProfile
        ? { ...WAR_ROOM_PROFILE_USERNAME_PAGE, params: { username: this.currentUser?.username ?? '' } }
        : WAR_ROOM_PROFILE_EDIT_PAGE;
    },
    position(): string {
      return this.currentUser?.warRoomPosition || '';
    },
  },
});
</script>

<style lang="scss" scoped>
.header-anonymous-profile {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;

  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 12px);
    right: 0;
    background-image: url('./assets/angle-right-b.svg');
    width: 24px;
    height: 24px;
  }

  &__avatar {
    margin-right: 8px;

    &--has-avatar {
      @include caption1-semibold;

      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background-color: var(--theme-color-border);
      color: var(--gray-50);
      overflow: hidden;
      border-radius: 50%;
    }
  }

  &__icon {
    color: var(--theme-color-text);
  }

  &__profile {
    flex: 1;
    color: var(--theme-color-text);
  }

  &__description {
    color: var(--gray-50);
  }
}
</style>
